import { useCallback, useEffect, useState } from "react";
import {
    AlertTriangle,
    CheckCircle2,
    KeyRound,
    Link2,
    Loader2,
    LogOut,
    Mail,
    RefreshCw,
    ShieldCheck,
    Trash2,
    Users,
} from "lucide-react";
import { Badge, Button, Input, Spinner } from "@discordforge/ui";
import {
    adminClearCache,
    adminPasswordUpdate,
    bridgeAccounts,
    bridgeAliases,
    bridgeCreateAccount,
    bridgeCreateAlias,
    bridgeDeleteAccount,
    bridgeDeleteAlias,
    bridgeQuotas,
    bridgeSetPassword,
    bridgeSetQuota,
    type BridgeAccount,
    type BridgeAlias,
    type BridgeQuota,
} from "./api";

type Tab = "accounts" | "aliases" | "settings";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function Status({ status }: { status: { kind: "ok" | "error"; text: string } | null }) {
    if (!status) return null;
    return (
        <span
            className={`flex items-center gap-1.5 text-xs font-medium ${
                status.kind === "ok" ? "text-emerald-400" : "text-red-400"
            }`}
        >
            {status.kind === "ok" ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />}
            {status.text}
        </span>
    );
}

export function AdminPanel({ onLogout }: { onLogout: () => void }) {
    const [tab, setTab] = useState<Tab>("accounts");

    return (
        <div className="flex h-screen overflow-hidden bg-[#0b0b0f] text-white">
            {/* Sidebar */}
            <aside className="flex w-56 shrink-0 flex-col border-r border-white/10 bg-[#0a0a0b]">
                <div className="flex items-center gap-2.5 px-4 pb-4 pt-5">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-orange-500/20 bg-orange-500/10 text-orange-400">
                        <ShieldCheck size={15} />
                    </div>
                    <div className="leading-tight">
                        <div className="text-sm font-black tracking-tight">Admin</div>
                        <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">
                            DiscordForge Mail
                        </div>
                    </div>
                </div>

                <nav className="flex-1 space-y-0.5 px-2.5">
                    {(
                        [
                            ["accounts", "Accounts", Users],
                            ["aliases", "Aliases", Link2],
                            ["settings", "Settings", KeyRound],
                        ] as const
                    ).map(([key, label, Icon]) => (
                        <button
                            key={key}
                            type="button"
                            onClick={() => setTab(key)}
                            className={`flex w-full cursor-pointer items-center gap-2.5 rounded-xl px-3 py-2 text-left text-[13px] font-medium transition-all ${
                                tab === key
                                    ? "border border-orange-500/25 bg-orange-500/10 text-orange-400"
                                    : "border border-transparent text-zinc-400 hover:bg-white/5 hover:text-zinc-100"
                            }`}
                        >
                            <Icon size={15} className="opacity-70" />
                            {label}
                        </button>
                    ))}
                </nav>

                <div className="space-y-0.5 border-t border-white/10 p-3">
                    <button
                        type="button"
                        onClick={() => {
                            window.location.hash = "#/";
                        }}
                        className="flex w-full cursor-pointer items-center gap-2.5 rounded-xl px-3 py-2 text-left text-[13px] font-medium text-zinc-400 transition-colors hover:bg-white/5 hover:text-zinc-100"
                    >
                        <Mail size={15} />
                        Back to mail
                    </button>
                    <button
                        type="button"
                        onClick={onLogout}
                        className="flex w-full cursor-pointer items-center gap-2.5 rounded-xl px-3 py-2 text-left text-[13px] font-medium text-zinc-400 transition-colors hover:bg-white/5 hover:text-red-400"
                    >
                        <LogOut size={15} />
                        Sign out
                    </button>
                </div>
            </aside>

            {/* Content */}
            <main className="custom-scrollbar min-w-0 flex-1 overflow-y-auto">
                <div className="mx-auto max-w-4xl px-8 py-8">
                    {tab === "accounts" && <AccountsTab />}
                    {tab === "aliases" && <AliasesTab />}
                    {tab === "settings" && <SettingsTab />}
                </div>
            </main>
        </div>
    );
}

/* ================================ Accounts ================================ */

function AccountsTab() {
    const [accounts, setAccounts] = useState<BridgeAccount[]>([]);
    const [quotas, setQuotas] = useState<BridgeQuota[]>([]);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
    const [busy, setBusy] = useState(false);

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [passwordFor, setPasswordFor] = useState<string | null>(null);
    const [newPassword, setNewPassword] = useState("");
    const [quotaFor, setQuotaFor] = useState<string | null>(null);
    const [quotaLimit, setQuotaLimit] = useState("");

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const [a, q] = await Promise.all([bridgeAccounts(), bridgeQuotas()]);
            setAccounts(a.accounts);
            setQuotas(q.quotas);
        } catch (err) {
            setStatus({ kind: "error", text: err instanceof Error ? err.message : "Failed to load accounts" });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const quotaOf = (email: string) => quotas.find((q) => q.email === email)?.limit ?? null;

    const createAccount = async () => {
        if (!EMAIL_RE.test(email) || password.length < 8) {
            setStatus({ kind: "error", text: "Valid email and password of at least 8 characters required." });
            return;
        }
        setBusy(true);
        setStatus(null);
        try {
            await bridgeCreateAccount(email, password);
            setStatus({ kind: "ok", text: `Account ${email} created` });
            setEmail("");
            setPassword("");
            await refresh();
        } catch (err) {
            setStatus({ kind: "error", text: err instanceof Error ? err.message : "Failed to create account" });
        } finally {
            setBusy(false);
        }
    };

    const deleteAccount = async (account: string) => {
        if (!window.confirm(`Delete ${account}? This removes the mailbox.`)) return;
        setStatus(null);
        try {
            await bridgeDeleteAccount(account);
            setStatus({ kind: "ok", text: `${account} deleted` });
            await refresh();
        } catch (err) {
            setStatus({ kind: "error", text: err instanceof Error ? err.message : "Failed to delete" });
        }
    };

    const changePassword = async (account: string) => {
        if (newPassword.length < 8) {
            setStatus({ kind: "error", text: "Password must be at least 8 characters." });
            return;
        }
        setStatus(null);
        try {
            await bridgeSetPassword(account, newPassword);
            setStatus({ kind: "ok", text: `Password updated for ${account}` });
            setPasswordFor(null);
            setNewPassword("");
        } catch (err) {
            setStatus({ kind: "error", text: err instanceof Error ? err.message : "Failed to update password" });
        }
    };

    const setQuota = async (account: string) => {
        const limit = Number(quotaLimit);
        if (!Number.isFinite(limit) || limit < 1) {
            setStatus({ kind: "error", text: "Quota must be a positive number (MiB)." });
            return;
        }
        setStatus(null);
        try {
            await bridgeSetQuota(account, limit);
            setStatus({ kind: "ok", text: `Quota ${limit} MiB set for ${account}` });
            setQuotaFor(null);
            await refresh();
        } catch (err) {
            setStatus({ kind: "error", text: err instanceof Error ? err.message : "Failed to set quota" });
        }
    };

    return (
        <section className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-black tracking-tight">Accounts</h1>
                    <p className="mt-0.5 text-sm text-zinc-500">Mailboxes on the mail server (docker-mailserver)</p>
                </div>
                <Button variant="dark" size="sm" onClick={refresh} disabled={loading}>
                    <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
                    Refresh
                </Button>
            </div>

            <Status status={status} />

            {/* create */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                <h2 className="text-sm font-black uppercase tracking-widest text-zinc-400">Create account</h2>
                <div className="mt-3 flex flex-wrap items-end gap-2">
                    <Input placeholder="new@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className="max-w-xs" />
                    <Input type="password" placeholder="Password (min 8 chars)" value={password} onChange={(e) => setPassword(e.target.value)} className="max-w-xs" />
                    <Button onClick={createAccount} disabled={busy}>
                        {busy ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}
                        Create
                    </Button>
                </div>
            </div>

            {/* list */}
            <div className="overflow-hidden rounded-2xl border border-white/10">
                {loading ? (
                    <div className="flex items-center justify-center py-16">
                        <Spinner size={22} label="Loading accounts…" />
                    </div>
                ) : accounts.length === 0 ? (
                    <div className="flex flex-col items-center gap-2 py-16 text-center">
                        <Users size={22} className="text-zinc-600" />
                        <p className="text-sm text-zinc-500">No accounts yet — create the first one above.</p>
                    </div>
                ) : (
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="border-b border-white/10 text-[10px] font-black uppercase tracking-widest text-zinc-500">
                                <th className="px-4 py-2.5">Email</th>
                                <th className="px-4 py-2.5">Mailbox</th>
                                <th className="px-4 py-2.5">Quota</th>
                                <th className="px-4 py-2.5 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {accounts.map((a) => (
                                <tr key={a.email} className="border-b border-white/[0.04] last:border-0">
                                    <td className="px-4 py-2.5 font-semibold text-zinc-100">{a.email}</td>
                                    <td className="px-4 py-2.5 tabular-nums text-zinc-400">
                                        {a.mailboxSizeMb > 0 ? `${a.mailboxSizeMb} MiB` : "—"}
                                    </td>
                                    <td className="px-4 py-2.5">
                                        {quotaOf(a.email) ? (
                                            <Badge variant="accent">{quotaOf(a.email)} MiB</Badge>
                                        ) : (
                                            <span className="text-xs text-zinc-600">none</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-2.5">
                                        <div className="flex items-center justify-end gap-1.5">
                                            {passwordFor === a.email ? (
                                                <span className="flex items-center gap-1.5">
                                                    <Input
                                                        type="password"
                                                        placeholder="New password"
                                                        value={newPassword}
                                                        onChange={(e) => setNewPassword(e.target.value)}
                                                        className="h-8 w-44 text-xs"
                                                        autoFocus
                                                    />
                                                    <Button size="sm" onClick={() => changePassword(a.email)}>
                                                        Set
                                                    </Button>
                                                    <Button size="sm" variant="ghost" onClick={() => setPasswordFor(null)}>
                                                        Cancel
                                                    </Button>
                                                </span>
                                            ) : quotaFor === a.email ? (
                                                <span className="flex items-center gap-1.5">
                                                    <Input
                                                        type="number"
                                                        placeholder="MiB"
                                                        value={quotaLimit}
                                                        onChange={(e) => setQuotaLimit(e.target.value)}
                                                        className="h-8 w-24 text-xs"
                                                        autoFocus
                                                    />
                                                    <Button size="sm" onClick={() => setQuota(a.email)}>
                                                        Set
                                                    </Button>
                                                    <Button size="sm" variant="ghost" onClick={() => setQuotaFor(null)}>
                                                        Cancel
                                                    </Button>
                                                </span>
                                            ) : (
                                                <>
                                                    <Button size="sm" variant="dark" title="Reset password" onClick={() => setPasswordFor(a.email)}>
                                                        <KeyRound size={12} />
                                                    </Button>
                                                    <Button size="sm" variant="dark" title="Set quota" onClick={() => setQuotaFor(a.email)}>
                                                        <Mail size={12} />
                                                    </Button>
                                                    <Button size="sm" variant="danger" title="Delete account" onClick={() => deleteAccount(a.email)}>
                                                        <Trash2 size={12} />
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </section>
    );
}

/* ================================= Aliases ================================ */

function AliasesTab() {
    const [aliases, setAliases] = useState<BridgeAlias[]>([]);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
    const [busy, setBusy] = useState(false);
    const [source, setSource] = useState("");
    const [destination, setDestination] = useState("");

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            setAliases((await bridgeAliases()).aliases);
        } catch (err) {
            setStatus({ kind: "error", text: err instanceof Error ? err.message : "Failed to load aliases" });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const create = async () => {
        if (!EMAIL_RE.test(source) || !EMAIL_RE.test(destination)) {
            setStatus({ kind: "error", text: "Valid source and destination emails required." });
            return;
        }
        setBusy(true);
        setStatus(null);
        try {
            await bridgeCreateAlias(source, destination);
            setStatus({ kind: "ok", text: `Alias ${source} → ${destination} created` });
            setSource("");
            setDestination("");
            await refresh();
        } catch (err) {
            setStatus({ kind: "error", text: err instanceof Error ? err.message : "Failed to create alias" });
        } finally {
            setBusy(false);
        }
    };

    const remove = async (alias: BridgeAlias) => {
        setStatus(null);
        try {
            await bridgeDeleteAlias(alias.source, alias.destination);
            setStatus({ kind: "ok", text: `Alias ${alias.source} removed` });
            await refresh();
        } catch (err) {
            setStatus({ kind: "error", text: err instanceof Error ? err.message : "Failed to delete alias" });
        }
    };

    return (
        <section className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-black tracking-tight">Aliases</h1>
                    <p className="mt-0.5 text-sm text-zinc-500">Forward an address to a mailbox</p>
                </div>
                <Button variant="dark" size="sm" onClick={refresh} disabled={loading}>
                    <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
                    Refresh
                </Button>
            </div>

            <Status status={status} />

            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                <h2 className="text-sm font-black uppercase tracking-widest text-zinc-400">Create alias</h2>
                <div className="mt-3 flex flex-wrap items-end gap-2">
                    <Input placeholder="info@example.com" value={source} onChange={(e) => setSource(e.target.value)} className="max-w-xs" />
                    <span className="pb-2 text-zinc-600">→</span>
                    <Input placeholder="test@example.com" value={destination} onChange={(e) => setDestination(e.target.value)} className="max-w-xs" />
                    <Button onClick={create} disabled={busy}>
                        {busy ? <Loader2 size={13} className="animate-spin" /> : <Link2 size={13} />}
                        Create
                    </Button>
                </div>
            </div>

            <div className="overflow-hidden rounded-2xl border border-white/10">
                {loading ? (
                    <div className="flex items-center justify-center py-16">
                        <Spinner size={22} label="Loading aliases…" />
                    </div>
                ) : aliases.length === 0 ? (
                    <div className="flex flex-col items-center gap-2 py-16 text-center">
                        <Link2 size={22} className="text-zinc-600" />
                        <p className="text-sm text-zinc-500">No aliases yet.</p>
                    </div>
                ) : (
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="border-b border-white/10 text-[10px] font-black uppercase tracking-widest text-zinc-500">
                                <th className="px-4 py-2.5">Source</th>
                                <th className="px-4 py-2.5">Destination</th>
                                <th className="px-4 py-2.5 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {aliases.map((a) => (
                                <tr key={`${a.source}-${a.destination}`} className="border-b border-white/[0.04] last:border-0">
                                    <td className="px-4 py-2.5 font-semibold text-zinc-100">{a.source}</td>
                                    <td className="px-4 py-2.5 text-zinc-400">{a.destination}</td>
                                    <td className="px-4 py-2.5 text-right">
                                        <Button size="sm" variant="danger" onClick={() => remove(a)}>
                                            <Trash2 size={12} />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </section>
    );
}

/* ================================ Settings ================================ */

function SettingsTab() {
    const [status, setStatus] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
    const [busy, setBusy] = useState(false);
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [login, setLogin] = useState("");

    const changePassword = async () => {
        if (next.length < 8 || !current) {
            setStatus({ kind: "error", text: "Current password and a new password (min 8 chars) are required." });
            return;
        }
        setBusy(true);
        setStatus(null);
        try {
            const res = await adminPasswordUpdate(login || "admin", current, next);
            setStatus({
                kind: "ok",
                text: "Admin password updated" + (res.Weak ? " (weak password detected)" : ""),
            });
            setCurrent("");
            setNext("");
        } catch (err) {
            setStatus({ kind: "error", text: err instanceof Error ? err.message : "Failed to update password" });
        } finally {
            setBusy(false);
        }
    };

    const clearCache = async () => {
        setBusy(true);
        setStatus(null);
        try {
            await adminClearCache();
            setStatus({ kind: "ok", text: "Webmail cache cleared" });
        } catch (err) {
            setStatus({ kind: "error", text: err instanceof Error ? err.message : "Failed to clear cache" });
        } finally {
            setBusy(false);
        }
    };

    return (
        <section className="flex flex-col gap-6">
            <div>
                <h1 className="text-xl font-black tracking-tight">Settings</h1>
                <p className="mt-0.5 text-sm text-zinc-500">Admin credentials and maintenance</p>
            </div>

            <Status status={status} />

            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                <h2 className="text-sm font-black uppercase tracking-widest text-zinc-400">Change admin password</h2>
                <div className="mt-3 flex flex-wrap items-end gap-2">
                    <Input placeholder="Admin login" value={login} onChange={(e) => setLogin(e.target.value)} className="max-w-[11rem]" />
                    <Input type="password" placeholder="Current password" value={current} onChange={(e) => setCurrent(e.target.value)} className="max-w-[11rem]" />
                    <Input type="password" placeholder="New password" value={next} onChange={(e) => setNext(e.target.value)} className="max-w-[11rem]" />
                    <Button onClick={changePassword} disabled={busy}>
                        {busy ? <Loader2 size={13} className="animate-spin" /> : <KeyRound size={13} />}
                        Update
                    </Button>
                </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                <h2 className="text-sm font-black uppercase tracking-widest text-zinc-400">Maintenance</h2>
                <div className="mt-3 flex items-center gap-2">
                    <Button variant="dark" onClick={clearCache} disabled={busy}>
                        {busy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                        Clear webmail cache
                    </Button>
                    <span className="text-xs text-zinc-600">Flushes the PHP data cache (config, templates)</span>
                </div>
            </div>
        </section>
    );
}
